import { useEffect } from "react";
import type { ReactNode } from "react";

let pageScrollLocks = 0;

function clearBodyScrollLock(body: HTMLElement) {
  const y = Number(body.dataset.scrollLockY || 0);
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.right = "";
  body.style.width = "";
  delete body.dataset.scrollLockY;
  window.scrollTo(0, y);
  window.dispatchEvent(new Event("resize"));
}

/** Keep the page and floor map from capturing scroll under a dialog. */
export function useLockPageScroll() {
  useEffect(() => {
    pageScrollLocks += 1;
    const body = document.body;
    document.documentElement.classList.add("modal-open");
    if (pageScrollLocks === 1) {
      body.dataset.scrollLockY = String(window.scrollY);
      body.style.position = "fixed";
      body.style.top = `-${body.dataset.scrollLockY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    }
    return () => {
      pageScrollLocks -= 1;
      if (pageScrollLocks <= 0) {
        pageScrollLocks = 0;
        document.documentElement.classList.remove("modal-open");
        clearBodyScrollLock(body);
      }
    };
  }, []);
}

export function CompanyBrowserModal({
  title,
  onClose,
  children,
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useLockPageScroll();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="company-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onWheel={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
    >
      <div
        className={className ? `company-shell company-modal ${className}` : "company-shell company-modal"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** View-level dossier shell for the Account split pane. Keeps the record visually above the workspace search/filter chrome. */
export function CompanyBrowserPane({
  title,
  onClose,
  children,
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className={className ? `company-shell company-pane ${className}` : "company-shell company-pane"}
      role="region"
      aria-label={title}
    >
      {children}
    </div>
  );
}
